import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import NumberField from "../../../components/Fields/NumberField";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { ApplicationFunctions, useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import CodeTaskParameterInfo from "../../../models/CodeTaskParameterInfo";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";
import Unauthorized from "../../Authentication/Unauthorized";
import CodeTaskField from "../Controls/CodeTaskField";

interface Props extends PopupProps {
    workflowDefinitionId
}

interface WorkflowEventCodeTaskCreateModel {
    workflowDefinitionId: number
    workflowEventTypeId: number
    codeTaskName: string
    parameters?: string
    executionOrder: number
    codeTaskParameterInfos: CodeTaskParameterInfo[]
}

const AddWorkflowEventCodeTask: React.FC<Props> = ({ show, onHide, workflowDefinitionId }) => {
    const [formData, setFormData] = useState<WorkflowEventCodeTaskCreateModel>({
        codeTaskParameterInfos: [],
        codeTaskName: '',
        executionOrder: 1,
        workflowEventTypeId: 0,
        parameters: '',
        workflowDefinitionId: workflowDefinitionId,
    });

    const eventTypes = useSelectListOptions('api/v1/selectlist/WorkflowEventTypes', '-- Select event type');
    const codeTaskNames = useSelectListOptions('api/v1/selectlist/CodeTaskNames', '-- Select codetask handler');
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowEventCodeTaskCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    function submit() {
        if (formData) {
            DataService
                .create<WorkflowEventCodeTaskCreateModel>(`api/v1/WorkflowEventCodeTasks/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data) {
                        onHide();
                    }

                })
                .catch(e => handleException(e));
        }
    }

    function setFieldValue(name: string, value: string) {
        name.split('.').reduce((object, propertyName, index, elements) => {
            if (index === elements.length - 1) {
                object[propertyName] = value;
                setFormData(() => { return { ...formData } });
            }

            if (object && object[propertyName]) {
                return object[propertyName];
            }
            return null;
        }, formData);
    }

    useEffect(() => {
        getCodeTaskParameterInfo(formData.codeTaskName)
    }, [formData.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName: string) {
        if (codeTaskName != '') {
            DataService
                .post<CodeTaskParameterInfo[]>(`api/v1/CodeTaskParameterInfos/GetByCodeTaskName/`, { codeTaskName: codeTaskName })
                .then(response => {
                    formData.codeTaskParameterInfos = response
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'workflowEventTypeId', label: 'Event type', validationRules: [{ type: 'isRequired' }] },
            { name: 'executionOrder', label: 'Execution order', validationRules: [{ type: 'isRequired' }] },
            { name: 'codeTaskName', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'parameters', label: 'Parameters' },
        ],
        handleSubmit: submit,
        handleFieldChanged: setFieldValue,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedForWorkflowDefinition } = useAuth();
    if (!isAuthorizedForWorkflowDefinition(ApplicationFunctions.UpdateWorkflowDefinition, workflowDefinitionId))
        return (<Unauthorized />);

    return (
        <Popup show={show} onHide={onHide} size={'lg'} title={'Add workflow event codetask'} >
            <Popup.Body>
                <SelectListField {...getFieldProps('workflowEventTypeId')} options={eventTypes} />
                <NumberField {...getFieldProps('executionOrder')} />
                <SelectListField {...getFieldProps('codeTaskName')} options={codeTaskNames} />
                {
                    formData.codeTaskParameterInfos.map((parInfo, index) =>
                        <CodeTaskField {...getFieldProps(`codeTaskParameterInfos.${index}.value`)} codeTaskParamterInfo={parInfo} />
                    )
                }
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default AddWorkflowEventCodeTask;